import { useCallback, useEffect } from 'react';
import { useCombatStore } from '../store/combatStore';
import { useWebSocket } from './useWebSocket';
import type { Spell, CombatMap, CombatEntity } from '../types/interfaces';
import { keepSocketOpen } from '../components/Mission/missionView';
import { effectiveWeather, fogObservers, fogRangeMax, hiddenByFog, type EffectiveWeather } from '../utils/weatherRules';

interface UseCombatOptions {
  combatId: string;
  myEntityId: string;
  toast?: {
    success: (t: string) => void;
    error: (t: string) => void;
    info: (t: string) => void;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// BFS: reachable cells with N MP
// ─────────────────────────────────────────────────────────────────────────────

/** Nível de elevação de uma célula — compatível com o legado 'High_Ground' (nível 1). */
function cellElevation(cell?: { type?: string; elevation?: number } | null): number {
  if (!cell) return 0;
  // -2..+2: negativo = Buraco/Vala, positivo = Plataforma Elevada/Montanha.
  if (typeof cell.elevation === 'number') return Math.max(-2, Math.min(2, Math.round(cell.elevation)));
  return cell.type === 'High_Ground' ? 1 : 0;
}

type ClimbCell = {
  type?: string;
  elevation?: number;
  climb_connection?: { targetX: number; targetY: number } | null;
};

function hasClimbConnectionTo(cell: ClimbCell | null | undefined, targetX: number, targetY: number): boolean {
  const conn = cell?.climb_connection;
  if (!conn) return false;
  return conn.targetX === targetX && conn.targetY === targetY;
}

/**
 * Sistema de Elevação de Terreno — Lógica de Colisão e Vias de Subida
 * (espelha MovementEngine::canTraverseElevation no backend). Qualquer
 * mudança de nível, mesmo de 1 só, é um degrau/muro bloqueado por padrão.
 * Só libera passagem: a) o tipo legado 'Ramp' numa das pontas; ou b) uma
 * `climb_connection` (Via de Subida Ponto A → Ponto B) explícita apontando
 * de uma célula para a outra.
 */
function canTraverseElevation(
  fromCell: ClimbCell | null | undefined,
  fromX: number,
  fromY: number,
  toCell: ClimbCell | null | undefined,
  toX: number,
  toY: number,
): boolean {
  const fromElev = cellElevation(fromCell);
  const toElev   = cellElevation(toCell);
  if (fromElev === toElev) return true;
  if (fromCell?.type === 'Ramp' || toCell?.type === 'Ramp') return true;
  return hasClimbConnectionTo(fromCell, toX, toY) || hasClimbConnectionTo(toCell, fromX, fromY);
}

function bfsReachable(
  ox: number,
  oy: number,
  mp: number,
  map: CombatMap,
  entities: Record<string, CombatEntity>,
  myId: string,
): Array<{ x: number; y: number }> {
  if (mp <= 0) return [];

  const queue: Array<[number, number, number]> = [[ox, oy, 0]];
  const visited = new Set<string>([`${ox},${oy}`]);
  const reachable: Array<{ x: number; y: number }> = [];

  const isBlocked = (x: number, y: number): boolean => {
    const cell = map.cells[y]?.[x];
    if (!cell) return true;
    if (cell.type === 'Lake') return true; // água sempre bloqueia, mesmo com elevação
    const elevation = cellElevation(cell);
    // Sistema de Elevação de Terreno: obstáculo decorativo só bloqueia ao
    // nível do chão (elevação 0) — no topo de uma montanha (elevação > 0)
    // o piso é sempre caminhável.
    if (elevation === 0 && ['Full_Obstacle', 'Tree', 'Mountain', 'Rock'].includes(cell.type)) return true;
    return Object.values(entities).some(
      (e) => e.id !== myId && e.position_x === x && e.position_y === y && e.hp > 0,
    );
  };

  while (queue.length > 0) {
    const [cx, cy, cost] = queue.shift()!;
    if (cost > 0) reachable.push({ x: cx, y: cy });
    if (cost >= mp) continue;

    for (const [dx, dy] of [
      [1, 0], [-1, 0], [0, 1], [0, -1],
    ] as const) {
      const nx = cx + dx;
      const ny = cy + dy;
      const key = `${nx},${ny}`;
      if (
        !visited.has(key) &&
        !isBlocked(nx, ny) &&
        canTraverseElevation(map.cells[cy]?.[cx], cx, cy, map.cells[ny]?.[nx], nx, ny)
      ) {
        visited.add(key);
        queue.push([nx, ny, cost + 1]);
      }
    }
  }
  return reachable;
}

function hasLineOfSight(x0: number, y0: number, x1: number, y1: number, map: CombatMap): boolean {
  if (x0 === x1 && y0 === y1) return true;
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;

  let cx = x0;
  let cy = y0;

  while (true) {
    if ((cx !== x0 || cy !== y0) && (cx !== x1 || cy !== y1)) {
      const cell = map.cells[cy]?.[cx];
      const type = cell?.type ?? 'Walkable';
      const elevation = cellElevation(cell);
      // Elevação > 0 = piso caminhável de uma plataforma; obstáculos
      // decorativos só bloqueiam visão ao nível do chão (ver MovementEngine).
      if (elevation === 0 && ['Full_Obstacle', 'Vision_Obstacle', 'Tree', 'Mountain', 'Rock'].includes(type)) {
        return false;
      }
    }

    if (cx === x1 && cy === y1) return true;

    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      cx += sx;
    }
    if (e2 < dx) {
      err += dx;
      cy += sy;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Spell range cells (Manhattan distance with Bresenham LoS on frontend)
// ─────────────────────────────────────────────────────────────────────────────

function calculateSpellRange(
  ox: number,
  oy: number,
  spell: Spell,
  map: CombatMap,
  weather: EffectiveWeather = 'NONE',
  observers: Array<{ x: number; y: number }> = [],
): Array<{ x: number; y: number }> {
  const result: Array<{ x: number; y: number }> = [];
  const casterElevation = cellElevation(map.cells[oy]?.[ox]);
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (map.cells[y]?.[x]?.type === 'Full_Obstacle') continue;
      const dist = Math.abs(x - ox) + Math.abs(y - oy);
      // Vantagem Tática de Elevação: espelha o bônus de alcance do
      // SpellEngine (backend) para o preview de células alcançáveis bater
      // com o que o servidor realmente vai aceitar — +1 de alcance por
      // nível de altura a mais que o atacante tem sobre a célula-alvo,
      // apenas para feitiços à distância (range_max > 1).
      const targetElevation = cellElevation(map.cells[y]?.[x]);
      const elevationAdvantage = Math.max(0, casterElevation - targetElevation);
      // Neblina (missões): mesmo corte de alcance que o servidor aplica antes da altura.
      const baseRangeMax = fogRangeMax(spell.range_min, spell.range_max, weather);
      const effectiveRangeMax = spell.range_max > 1 ? baseRangeMax + elevationAdvantage : baseRangeMax;
      if (dist >= spell.range_min && dist <= effectiveRangeMax) {
        if (spell.line_of_sight && !hasLineOfSight(ox, oy, x, y, map)) {
          continue;
        }
        if (hiddenByFog(x, y, observers, weather)) continue;
        result.push({ x, y });
      }
    }
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useCombat({ combatId, myEntityId, toast }: UseCombatOptions) {
  const store = useCombatStore();
  const { sendAction, disconnect, connected } = useWebSocket({ combatId, entityId: myEntityId });

  const myEntity   = store.getMyEntity();
  const myTurn     = store.isMyTurn();
  const selectedSpell = store.getSelectedSpell();

  // ── Recalculate movement highlights automatically ─────────────────────────
  // Runs whenever turn ownership, position, MP, or selected spell changes.
  // Also triggers when active_entity_id changes (i.e. after any turn transition)
  // to guarantee stale highlights are never left on the board.
  useEffect(() => {
    if (!store.combat) return;

    if (!myTurn || !myEntity) {
      store.clearHighlights();
      return;
    }

    if (selectedSpell) {
      // Show range of selected spell
      const cells = calculateSpellRange(
        myEntity.position_x,
        myEntity.position_y,
        selectedSpell,
        store.combat.map,
        effectiveWeather(store.combat),
        fogObservers(store.combat.entities, myEntity.team),
      );
      store.setHighlights(cells.map((c) => ({ ...c, type: 'spell_range' as const })));
    } else {
      // Show reachable movement cells (BFS)
      const cells = bfsReachable(
        myEntity.position_x,
        myEntity.position_y,
        myEntity.mp,
        store.combat.map,
        store.combat.entities,
        myEntity.id,
      );
      store.setHighlights(cells.map((c) => ({ ...c, type: 'movement' as const })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Use primitive values so React can compare correctly across renders.
    // store.combat itself is a new object reference every server push, which
    // would cause an infinite loop if used directly.
    myTurn,
    store.combat?.active_entity_id,
    store.combat?.current_turn,
    myEntity?.mp,
    myEntity?.ap,
    myEntity?.position_x,
    myEntity?.position_y,
    selectedSpell?.id,
    store.combat?.id,
  ]);

  // ── Sync myEntityId in store ──────────────────────────────────────────────
  useEffect(() => {
    store.setMyEntityId(myEntityId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEntityId]);

  // ── Troca de combate (ex.: "Nova Batalha" sem sair de /play) ──────────────
  // Zera qualquer estado da batalha anterior ANTES do socket novo abrir, para
  // o guard de loading do App não segurar a tela com um combate FINISHED velho
  // (e não disparar disconnect() no combate novo).
  useEffect(() => {
    const s = useCombatStore.getState();
    if (s.combat && s.combat.id !== combatId) {
      s.resetCombat();
      s.setMyEntityId(myEntityId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combatId]);

  // ── Disconnect WebSocket when combat is no longer active ──────────────────
  // Fires on FINISHED (server-driven) and ABANDONED (client-driven).
  // Closing the socket immediately frees the server-side connection slot
  // instead of waiting for the component to unmount.
  // disconnect() also suppresses the auto-reconnect timer so the closed
  // socket is not re-opened while the result/exit overlay is visible.
  useEffect(() => {
    const phase = store.combat?.phase;
    // Só desliga para ESTE combate — um combate FINISHED antigo ainda no store
    // (troca de batalha) não pode fechar o socket da batalha nova.
    // Exceção: nível de missão vencido com portal aberto — o jogador ainda
    // anda até o portal pelo socket (MOVE pós-vitória).
    if ((phase === 'FINISHED' || phase === 'ABANDONED') && store.combat?.id === combatId && !keepSocketOpen(store.combat)) {
      const timer = setTimeout(() => disconnect(), 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.combat?.phase, store.combat?.id, !!store.combat?.mission?.portal, combatId]);

  // ── Abandon (player exits an in-progress battle) ─────────────────────────
  // Stamps the local phase as ABANDONED and closes the WebSocket immediately.
  // The auto-disconnect effect above would do the same on the next render tick,
  // but calling disconnect() here is synchronous so the socket is freed before
  // React even processes the state update — important when navigate() follows
  // immediately (component may unmount before the next render).
  const abandon = useCallback(() => {
    sendAction('ABANDON');
    store.abandonCombat();
    // Allow a brief moment for the ABANDON frame to reach the server before closing
    setTimeout(() => {
      disconnect();
    }, 100);
  }, [store, disconnect, sendAction]);

  // ── Move ──────────────────────────────────────────────────────────────────
  const move = useCallback(
    (destX: number, destY: number) => {
      if (!myTurn || !myEntity || !store.combat) return;

      // Re-calculate reachable cells from current live state to avoid stale
      // highlight issues (the useEffect may not have re-run yet after a turn
      // transition where mp/position did not change).
      const reachable = bfsReachable(
        myEntity.position_x,
        myEntity.position_y,
        myEntity.mp,
        store.combat.map,
        store.combat.entities,
        myEntity.id,
      );

      const inRange = reachable.some((c) => c.x === destX && c.y === destY);
      if (!inRange) {
        toast?.error('Espaço inválido ou muito distante para mover!');
        return;
      }

      sendAction('MOVE', { dest_x: destX, dest_y: destY });
    },
    [myTurn, myEntity, store.combat, sendAction, toast],
  );

  // ── Caminhada pós-vitória (missão com portal aberto) ──────────────────────
  // Sem turno nem PM: o servidor só aceita enquanto o portal do nível estiver aberto.
  const walkTo = useCallback(
    (destX: number, destY: number) => { sendAction('MOVE', { dest_x: destX, dest_y: destY }); },
    [sendAction],
  );

  // ── Cast Spell ────────────────────────────────────────────────────────────
  const cast = useCallback(
    (targetX: number, targetY: number, targetId?: string) => {
      if (!myTurn || !selectedSpell) return;

      // Verify cell is within spell range
      const inRange = store.ui.highlights.some(
        (h) => h.x === targetX && h.y === targetY,
      );
      if (!inRange) {
        toast?.error('Alvo fora do alcance da magia!');
        return;
      }

      sendAction('CAST_SPELL', {
        spell_id: selectedSpell.id,
        target_x: targetX,
        target_y: targetY,
        target_id: targetId,
      });
      store.selectSpell(null);
    },
    [myTurn, selectedSpell, store.ui.highlights, sendAction, store, toast],
  );

  // ── End Turn ──────────────────────────────────────────────────────────────
  const endTurn = useCallback(() => {
    if (!myTurn) return;
    store.selectSpell(null);
    sendAction('END_TURN');
  }, [myTurn, sendAction, store]);

  // ── Use Item (consumível) — gasta o turno, como encerrar a jogada ─────────
  const useItem = useCallback(
    (itemId: string) => {
      if (!myTurn || !itemId) return;
      store.selectSpell(null);
      sendAction('USE_ITEM', { item_id: itemId });
    },
    [myTurn, sendAction, store],
  );

  // ── Select / Deselect Spell ───────────────────────────────────────────────
  const selectSpell = useCallback(
    (spell: Spell | null) => {
      if (!myTurn) return;

      if (spell === null || store.ui.selectedSpellId === spell.id) {
        store.selectSpell(null);
        return;
      }
      store.selectSpell(spell.id);
      // The useEffect above recalculates highlights automatically
    },
    [myTurn, store],
  );

  // ── Grid Events ───────────────────────────────────────────────────────────
  const onCellHover = useCallback(
    (x: number, y: number) => {
      store.setCellHover(x, y);
    },
    [store],
  );

  const onCellClick = useCallback(
    (x: number, y: number) => {
      if (!myTurn || !store.combat) return;

      if (selectedSpell) {
        // Spell mode — find target entity at cell
        const target = Object.values(store.combat.entities).find(
          (e) => e.position_x === x && e.position_y === y && e.hp > 0,
        );
        cast(x, y, target?.id);
      } else {
        // Movement mode
        move(x, y);
      }
    },
    [myTurn, selectedSpell, store.combat, cast, move],
  );

  return {
    combat:          store.combat,
    myEntity,
    myTurn,
    selectedSpell,
    highlights:      store.ui.highlights,
    cellHover:       store.ui.cellHover,
    connected,

    abandon,
    move,
    walkTo,
    cast,
    endTurn,
    useItem,
    selectSpell,
    onCellHover,
    onCellClick,
  };
}
